import { useState, useEffect, useMemo, FormEvent } from 'react'
import { login, fetchPublicServers, type PublicServer } from '../api'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ExternalLink } from 'lucide-react'
import { CreepLogo } from './CreepLogo'
import { CreepMark } from './CreepMark'
import { resolveServerIcon } from './server-icons'

interface Props {
  onSuccess: () => void
}

// Косметика: "gila1" → "Gila — 1", "andy" → "Andy".
function prettySub(raw: string): string {
  if (!raw) return ''
  const m = /^([A-Za-zА-Яа-яЁё][A-Za-zА-Яа-яЁё_-]*?)(\d+)$/.exec(raw)
  if (m) return `${m[1].charAt(0).toUpperCase()}${m[1].slice(1)} — ${m[2]}`
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

export default function LoginPage({ onSuccess }: Props) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [servers, setServers] = useState<PublicServer[]>([])

  const currentHost = typeof window !== 'undefined' ? window.location.hostname : ''

  // Список соседних серверов клиента для быстрого перехода БЕЗ авторизации.
  // Источник №1 — публичный endpoint (по серверному лиц-ключу панели, без
  // секретов). Источник №2 (fallback) — localStorage, заполняемый
  // ServerSwitcher'ом когда юзер был залогинен на этом origin.
  useEffect(() => {
    let alive = true
    try {
      const cached = localStorage.getItem('cb-known-servers')
      if (cached) {
        const arr = JSON.parse(cached)
        if (Array.isArray(arr) && arr.length > 0) setServers(arr)
      }
    } catch { /* ignore */ }
    fetchPublicServers().then(list => {
      if (alive && list.length > 0) setServers(list)
    })
    return () => { alive = false }
  }, [])

  // Другие серверы (исключая текущий). Блок скрыт, если всего известно
  // < 2 серверов (т.е. переключаться не на что).
  const otherServers = useMemo(
    () => servers.filter(s => s.domain && s.domain !== currentHost),
    [servers, currentHost],
  )
  const showSwitch = servers.length >= 2 && otherServers.length > 0

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await login(username, password)
      if (res.ok) {
        onSuccess()
      } else {
        setError(res.error || 'Ошибка авторизации')
      }
    } catch {
      setError('Ошибка сети')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-center gap-2.5 bg-background p-4">
      {showSwitch && (
        <div className="w-[340px] max-w-full animate-modal-in">
          <div className="rounded-lg border border-border bg-card/60 p-2.5">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground/60 px-1 pb-1.5">
              Другие серверы
            </div>
            <div className="flex flex-col gap-1 max-h-[200px] overflow-y-auto">
              {otherServers.map(s => {
                const Icon = resolveServerIcon(s.server_icon)
                return (
                  <button
                    key={s.domain}
                    type="button"
                    onClick={() => { if (s.url) window.location.href = s.url }}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-border/60 bg-card/40 hover:bg-accent/50 transition-colors cursor-pointer text-left"
                  >
                    <Icon className="w-4 h-4 shrink-0 text-primary" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-1 min-w-0">
                        <span className="text-sm font-medium truncate">
                          {prettySub(s.subdomain || s.domain)}
                        </span>
                        <span className="text-[10px] text-muted-foreground/70 truncate">.crp.pro</span>
                      </div>
                      {s.server_name && (
                        <div className="text-[11px] text-muted-foreground truncate leading-tight">
                          {s.server_name}
                        </div>
                      )}
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}
      <Card className="w-[340px] max-w-full animate-modal-in">
        <form onSubmit={handleSubmit}>
          <CardHeader className="items-center gap-3 pb-4">
            <CreepMark size={52} />
            <CreepLogo size={22} />
            <span className="text-xs text-muted-foreground">Войдите для продолжения</span>
          </CardHeader>

          <CardContent className="space-y-4">
            {error && (
              <div className="text-xs text-center py-2 px-3 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
                {error}
              </div>
            )}

            <Input
              type="text"
              placeholder="Логин"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoFocus
            />

            <Input
              type="password"
              placeholder="Пароль"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />

            <Button type="submit" className="w-full" disabled={loading || !username || !password}>
              {loading ? 'Вход...' : 'Войти'}
            </Button>
          </CardContent>
        </form>
      </Card>
    </div>
  )
}
